import React, { useState } from "react"
import Badge  from "@react/shared/ui/Badge"
import InvoiceTypeModal from "./InvoiceTypeModal"
import { fmt, dateFmt } from "../helpers/format"
import ActionButton from "./ActionButton"

const GRID = "grid grid-cols-[120px_minmax(0,2fr)_120px_140px_130px] gap-4"

const StatTile = ({ label, value, tone }) => (
  <div className={`px-4 py-3 border rounded-xl ${tone || "bg-slate-50 border-slate-200"}`}>
    <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
    <div className="text-[18px] font-semibold tabular-nums text-slate-900">{value}</div>
  </div>
)

const InvoicesSection = ({ standardInvoicing }) => {
  const [modalOpen, setModalOpen] = useState(false)
  if (!standardInvoicing) return null
  const invoices = standardInvoicing.invoices || []

  return (
    <section id="section-invoices" className="px-7 scroll-mt-24">
      <header className="pl-[16px] mb-3 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900">{I18n.t("projects.invoices.section.title")}</h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">{I18n.t("projects.invoices.section.subtitle")}</p>
        </div>
        {standardInvoicing.can_create && (
          <ActionButton onClick={() => setModalOpen(true)}>
            {I18n.t("projects.invoices.banner.cta")}
          </ActionButton>
        )}
      </header>

      <div className="grid gap-3 grid-cols-1 md:grid-cols-3 mb-4">
        <StatTile label={I18n.t("projects.invoices.section.contract")}  value={fmt(standardInvoicing.contract_total)} />
        <StatTile label={I18n.t("projects.invoices.section.invoiced")}  value={fmt(standardInvoicing.invoiced_total)}  tone="bg-blue-50/60 border-blue-200" />
        <StatTile label={I18n.t("projects.invoices.section.remaining")} value={fmt(standardInvoicing.remaining_total)} tone="bg-amber-50/60 border-amber-200" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className={`${GRID} px-5 py-2.5 bg-slate-50 border-b border-slate-200 text-[10.5px] uppercase tracking-wider text-slate-500 font-medium`}>
          <div>{I18n.t("projects.invoices.section.number")}</div>
          <div>{I18n.t("projects.invoices.section.title_col")}</div>
          <div>{I18n.t("projects.invoices.section.date")}</div>
          <div>{I18n.t("projects.invoices.section.status")}</div>
          <div className="text-right">{I18n.t("projects.invoices.section.amount")}</div>
        </div>

        {invoices.length === 0 ? (
          <div className="px-6 py-12 text-center text-[13px] text-slate-500">{I18n.t("projects.invoices.section.empty")}</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {invoices.map(invoice => (
              <a
                key       = {invoice.id}
                href      = {invoice.path}
                className = {`${GRID} px-5 py-3 items-center text-[12.5px] hover:bg-slate-50/70 transition-colors`}
              >
                <div className="font-mono text-[11.5px] text-slate-500">{invoice.number || "—"}</div>
                <div className="text-slate-900 font-medium truncate" title={invoice.title}>{invoice.title || "—"}</div>
                <div className="text-slate-500 tabular-nums">{invoice.creation_date ? dateFmt(invoice.creation_date) : "—"}</div>
                <div><Badge tone={invoice.status_tone || "neutral"} variant="soft" size="sm">{invoice.status_label}</Badge></div>
                <div className="text-right tabular-nums text-slate-900 font-semibold">{fmt(invoice.total)}</div>
              </a>
            ))}
          </div>
        )}
      </div>

      <InvoiceTypeModal
        isOpen            = {modalOpen}
        standardInvoicing = {standardInvoicing}
        onClose           = {() => setModalOpen(false)}
      />
    </section>
  )
}

export default InvoicesSection
